import { useCallback, useEffect, useState } from "react";
import { Flame, Gift, Loader2, Mail, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, errorDetail } from "@/lib/api";

// The monthly free months for the members who bring friends, and MiLu's rewatch nudge.
export const SharerPrizes = () => {
  const [board, setBoard] = useState(null);
  const [awards, setAwards] = useState([]);
  const [warmth, setWarmth] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/referrals/leaderboard").then(({ data }) => setBoard(data))
      .catch((e) => console.error("could not load the sharers' board", e));
    api.get("/admin/referrals/awards").then(({ data }) => setAwards(data.awards || []))
      .catch((e) => console.error("could not load the sharer awards", e));
    api.get("/admin/hall/warm").then(({ data }) => setWarmth(data))
      .catch((e) => console.error("could not check the wall's languages", e));
  }, []);

  useEffect(load, [load]);

  const give = async () => {
    if (!window.confirm(
      `Give ${board?.prize_days || 30} free days of Premium to the top ${board?.winners || 3} `
      + `sharers of ${board?.month || "this month"}? Real accounts are upgraded straight away.`)) {
      return;
    }
    setBusy("prize");
    try {
      const { data } = await api.post("/admin/referrals/reward");
      toast.success(data.winners.length
        ? `${data.winners.length} free month${data.winners.length === 1 ? "" : "s"} given`
        : "Nothing new to give — this month is already settled");
      load();
    } catch (e) {
      toast.error(errorDetail(e) || "The prizes could not be given out");
    } finally {
      setBusy("");
    }
  };

  const warmWall = async () => {
    setBusy("warm");
    try {
      const { data } = await api.post("/admin/hall/warm");
      toast.success(data.written.length
        ? `The wall is now written in ${data.written.join(", ")}`
        : "Every busy language is already written");
      load();
    } catch (e) {
      toast.error(errorDetail(e) || "The wall could not be warmed");
    } finally {
      setBusy("");
    }
  };

  const nudge = async () => {
    setBusy("nudge");
    try {
      const { data } = await api.post("/admin/mailouts/tour");
      toast.success(`${data.sent} of ${data.waiting} rewatch nudges sent`);
    } catch (e) {
      toast.error(errorDetail(e) || "The nudges could not go out");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="rounded-3xl border border-white/10 bg-panel p-6" data-testid="sharer-prizes">
      <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-ink-soft">
        <Trophy className="h-4 w-4 text-brand" /> Sharers and rewatch nudges
      </p>

      {!board ? (
        <Loader2 className="mt-4 h-4 w-4 animate-spin text-ink-soft" />
      ) : (
        <>
          <p className="mt-3 text-sm text-ink-soft" data-testid="sharer-prizes-month">
            {board.month} · {board.sharers} member{board.sharers === 1 ? "" : "s"} brought someone ·
            top {board.winners} win {board.prize_days} days each
          </p>
          <ul className="mt-3 space-y-1.5">
            {board.top.slice(0, 5).map((row) => (
              <li key={row.user_id} className="flex items-center gap-2 text-[11px]"
                  data-testid={`admin-sharer-${row.rank}`}>
                <span className="w-5 shrink-0 tabular-nums text-ink-soft">{row.rank}</span>
                <span className="w-48 shrink-0 truncate text-ink">{row.name}</span>
                <span className="tabular-nums text-ink-soft">
                  {row.friends} this month · {row.friends_ever} ever
                  {row.won ? " · already rewarded" : ""}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="mt-5 flex flex-wrap gap-2">
        <button
          data-testid="sharer-prizes-give"
          onClick={give}
          disabled={busy === "prize"}
          className="inline-flex min-h-[36px] items-center gap-2 rounded-full bg-brand px-4 text-xs font-semibold text-white transition-transform hover:bg-brand-hover active:scale-95 disabled:opacity-50"
        >
          <Gift className="h-3.5 w-3.5" />
          {busy === "prize" ? "Giving…" : "Give this month's free months"}
        </button>
        <button
          data-testid="tour-rewatch-send"
          onClick={nudge}
          disabled={busy === "nudge"}
          className="inline-flex min-h-[36px] items-center gap-2 rounded-full border border-white/15 px-4 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-brand disabled:opacity-50"
        >
          <Mail className="h-3.5 w-3.5" />
          {busy === "nudge" ? "Sending…" : "Send the rewatch nudges now"}
        </button>
        <button
          data-testid="hall-warm"
          onClick={warmWall}
          disabled={busy === "warm"}
          className="inline-flex min-h-[36px] items-center gap-2 rounded-full border border-white/15 px-4 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-brand disabled:opacity-50"
        >
          <Flame className="h-3.5 w-3.5" />
          {busy === "warm" ? "Warming…" : "Warm the wall's languages"}
        </button>
      </div>

      {warmth && (
        <p className="mt-3 text-[11px] text-ink-soft" data-testid="hall-warmth">
          The wall reads in {warmth.ready.length} language{warmth.ready.length === 1 ? "" : "s"}
          {warmth.waiting.length
            ? ` · still waiting: ${warmth.waiting.join(", ")}`
            : " · every busy language is ready"}
        </p>
      )}

      {awards.length > 0 && (
        <ul className="mt-4 space-y-1 text-[11px] text-ink-soft" data-testid="sharer-awards">
          {awards.slice(0, 6).map((a) => (
            <li key={`${a.month}-${a.user_id}`}>
              {a.month} · #{a.rank} {a.name} · {a.friends} friends · {a.days} days
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
